import { useCallback, useMemo } from 'react';
import { dateToApiFormat } from '../../helpers/dateHelper';
import { useTasks } from '../../services/tasks/useTasks';
import { FoundShiftsInDateRange, useFindReassignedShiftsInDateRangeQuery } from '../gql/graphql.generated';
import { useSystemSettings } from '../settings/SystemSettingsProvider';
import { useMyLocation } from '../useLocations';
import { applyReassignedDataToSummary } from './buildReassignedSummary';

export const useReassignedShiftSummary = (startDate: Date, endDate: Date) => {
  // 1. ALL HOOKS AT THE TOP (Satisfies React Hook Rules)
  const selectedLocationId = useMyLocation().id;
  const tasks = useTasks();
  const includeBreaks = useSystemSettings(s => s.settings.includeBreaksInTotals);

  // 2. FETCH DATA NATURALLY
  // Suspense will catch the Promise and pause the component here if data is missing.
  const [{ data: reassignedShiftData }] = useFindReassignedShiftsInDateRangeQuery({
    variables: {
      searchParams: {
        homeLocationId: selectedLocationId,
        start: dateToApiFormat(startDate),
        end: dateToApiFormat(endDate)
      }
    },
    requestPolicy: 'cache-and-network'
  });

  // 3. NO MORE THROWING ERRORS!
  // By the time this line runs, Suspense guarantees the data has arrived.
  const reassignedShiftDataInRange = useMemo(
    () => reassignedShiftData!.findReassignedShiftsInDateRange as FoundShiftsInDateRange[],
    [reassignedShiftData]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addReassignedShifts = useCallback(
    applyReassignedDataToSummary(reassignedShiftDataInRange, tasks, selectedLocationId, includeBreaks),
    [reassignedShiftDataInRange, selectedLocationId, tasks, includeBreaks]
  );

  return { addReassignedShifts };
};
